import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import './i18n';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { store, persistor } from './redux/store';
import { UserProvider } from './contexts/UserContext';
import { Toaster } from 'react-hot-toast';
import { QueryClient, QueryClientProvider, QueryCache } from '@tanstack/react-query';
import { persistQueryClient } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';

// First create a query cache with callbacks
const queryCache = new QueryCache({
  onError: (error, query) => {
    console.log(`[DEBUG-QueryClient] Error in query: ${JSON.stringify(query.queryKey)}:`, error);
  },
  onSuccess: (data, query) => {
    console.log(`[DEBUG-QueryClient] Success in query: ${JSON.stringify(query.queryKey)}, data:`, 
      query.queryKey[0] === 'availableMenuTypes' ? data : 'data-hidden-to-reduce-log-size');
  }
});

// Configure React Query client with improved settings
const queryClient = new QueryClient({
  queryCache,
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 15, // Increase to 15 minutes (from 5)
      cacheTime: 1000 * 60 * 30, // Increase to 30 minutes (from 10)
      refetchOnWindowFocus: false, // Disable refetch on focus (was true)
      retry: (failureCount, error) => {
        // Only retry network errors, not server errors
        return failureCount < 2 && error?.message?.includes('network');
      },
    },
  }
});

// Create a local storage persister for React Query cache
const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: 'school-lunch-app-cache', // Custom key for the cache in localStorage
  throttleTime: 1000, // Only persist at most once per second
});

// Enable cache persistence to survive page refreshes
persistQueryClient({
  queryClient,
  persister,
  maxAge: 1000 * 60 * 60 * 24, // 24 hours
  dehydrateOptions: {
    // Don't persist queries older than 15 minutes (reduced from 1 hour)
    shouldDehydrateQuery: (query) => {
      // Don't persist availableMenuTypes queries at all - always fetch fresh
      if (query.queryKey[0] === 'availableMenuTypes') {
        return false;
      }
      // For other queries, only persist if they're recent
      return query.state.dataUpdatedAt >= Date.now() - 1000 * 60 * 15;
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <UserProvider>
            <App />
            <Toaster 
              position="top-left" 
              toastOptions={{
                style: {
                  zIndex: 40
                },
                custom: {
                  first: (t) => ({
                    marginTop: '4rem',
                  }),
                  others: () => ({})
                }
              }}
              gutter={8}
            />
          </UserProvider>
        </PersistGate>
      </Provider>
    </QueryClientProvider>
  </React.StrictMode>
); 